import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { ROLES } from '../../constants/roles';
import { SaldosClientesProvider, useSaldosClientes } from '../../hooks/useSaldosClientes';
import { useRefrescoPadronAlEntrar } from '../../hooks/useRefrescoPadronAlEntrar';
import ProductorHeader from './ProductorHeader';
import MisClientesTab from '../clientes/MisClientesTab';
import { refrescarMisClientes } from '../../services/productoresService';
import DashboardTab from '../DashboardTab';
import ControlSaldosTab from '../ControlSaldosTab';
import OperacionesTab from '../OperacionesTab';
import CaucionesTab from '../CaucionesTab';
import InstruccionesTab from '../InstruccionesTab';
import LicitacionesTab from '../LicitacionesTab';
import CaucionFormModal from '../CaucionFormModal';
import CommentModal from '../CommentModal';
import UpdateBanner from '../UpdateBanner';
import { MainContent, MainTabBar } from '../common/MainTabs';
import { usePersistentTab } from '../../hooks/useTabBar';
import { subscribeByComitentes } from '../../services/comitentesQueryService';
import { getProductorAttachmentFolder } from '../../utils/instruccionesAttachments';

// Jefferies queda afuera: son cuentas de custodia en el exterior de un padrón
// interno, no comitentes del productor.
const MERCADOS_PRODUCTOR = ['BYMA', 'MAV', 'A3', 'SENEBI', 'EXTERIOR'];

const PRODUCTOR_TABS = [
    { key: 'clientes', label: 'Mis Clientes' },
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'saldos', label: 'Control de Saldos' },
    { key: 'operaciones', label: 'Operaciones' },
    { key: 'cauciones', label: 'Cauciones/Pases' },
    { key: 'instrucciones', label: 'Instrucciones' },
    { key: 'licitaciones', label: 'Licitaciones' },
];
const PRODUCTOR_TAB_KEYS = PRODUCTOR_TABS.map(tab => tab.key);

const AppWrapper = styled.div`
  background-color: ${props => props.theme.colors.background};
  /* #root ya reserva los márgenes seguros del celular (ver GlobalStyles). */
  min-height: calc(100vh - env(safe-area-inset-top) - env(safe-area-inset-bottom));
`;
// Los títulos A3 del productor salen de sus propios docs `saldos` (ya filtrados
// por el proveedor), para el aviso de venta con títulos en A3.
const ProductorOperaciones = (props) => {
    const { initialSaldos } = useSaldosClientes();
    const titulosA3 = useMemo(() => {
        const titulos = [];
        initialSaldos.forEach(saldo => {
            (Array.isArray(saldo.titulosA3) ? saldo.titulosA3 : []).forEach(titulo => {
                titulos.push({
                    nroComitente: saldo.nroComitente,
                    nombreCliente: saldo.nombre,
                    codigoEspecie: titulo.codigoEspecie,
                    nombreEspecie: titulo.nombreEspecie,
                    cantidad: titulo.cantidad,
                });
            });
        });
        return titulos;
    }, [initialSaldos]);
    return <OperacionesTab {...props} titulosA3={titulosA3} />;
};

/**
 * Planilla de un productor externo. Reutiliza las pestañas de Operaciones y
 * Cauciones/Pases con el rol Comercial (misma carga, mismas validaciones), pero
 * con todas las lecturas acotadas a los comitentes del productor. El padrón de
 * clientes es el que trae el Sistema de Gestión Comercial en el doc del productor.
 *
 * Dashboard y Control de Saldos son los mismos componentes que usa la mesa: el
 * proveedor de saldos en modo productor acota los datos a sus comitentes y las
 * dos pestañas se muestran en modo lectura (sin carga del archivo del custodio,
 * movimientos A3 ni historial de snapshots).
 */
const ProductorView = ({ user, productor, onSignOut, updateAvailable, onReloadNow }) => {
    const [activeTab, setActiveTab] = usePersistentTab('planilla.productor.activeTab', 'clientes', PRODUCTOR_TAB_KEYS);
    const [especies, setEspecies] = useState([]);
    const [clientesPadron, setClientesPadron] = useState([]);
    const [caucionModal, setCaucionModal] = useState({ open: false, prefill: null });
    const [commentModalData, setCommentModalData] = useState({ isOpen: false, title: '', content: '' });

    // Al entrar se sincroniza el padrón contra el SGC si la última corrida quedó
    // vieja, así un cliente recién asignado allá ya está disponible para cargarle
    // órdenes sin esperar la corrida horaria.
    useRefrescoPadronAlEntrar(user, productor);

    // El nombre del SGC identifica al productor en la columna "Comercial" que ve la mesa.
    const userName = productor.nombre || user.displayName || user.email;
    const comitentesKey = (productor.comitentes || []).join('|');

    const clientesDb = useMemo(() => (productor.clientes || []).reduce((acc, c) => {
        acc[c.nroComitente] = c.nombre;
        return acc;
    }, {}), [productor.clientes]);

    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'especies'), (snapshot) => {
            setEspecies(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        }, (error) => console.error('Error al obtener las especies:', error));
        return () => unsubscribe();
    }, []);

    // Padrón interno de sus clientes: aporta CUIT y tipo de documento ya conocidos
    // para autocompletar las órdenes de licitación.
    useEffect(() => {
        const comitentes = comitentesKey ? comitentesKey.split('|') : [];
        return subscribeByComitentes('clientes', comitentes, setClientesPadron);
    }, [comitentesKey]);

    const datosFiscalesDb = useMemo(() => clientesPadron.reduce((acc, client) => {
        if (client.cuit || client.tipoDocumento) {
            acc[client.nroComitente] = {
                cuit: client.cuit || '',
                tipoDocumento: client.tipoDocumento || '',
            };
        }
        return acc;
    }, {}), [clientesPadron]);

    const handleViewComment = (title, content) => setCommentModalData({ isOpen: true, title, content });

    const tabsComunes = {
        userRole: ROLES.COMERCIAL,
        userName,
        userId: user.uid,
        clientesDb,
        especies,
        onViewComment: handleViewComment,
        comitentesFilter: productor.comitentes,
    };

    return (
        <SaldosClientesProvider user={user} clientesDb={clientesDb} comitentesFilter={productor.comitentes}>
            <AppWrapper>
                {updateAvailable && <UpdateBanner onReloadNow={onReloadNow} />}
                <ProductorHeader user={user} productor={productor} onSignOut={onSignOut} />
                <MainContent>
                    <MainTabBar tabs={PRODUCTOR_TABS} activeTab={activeTab} onChange={setActiveTab} />

                    {activeTab === 'clientes' && (
                        <MisClientesTab
                            cartera={productor}
                            onRefrescar={() => refrescarMisClientes(user)}
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                            avisoSinCuentas={(
                                <>
                                    Tus clientes se asignan en el sistema comercial: pedile a Axon Capital que te los asigne
                                    y después tocá <strong>Actualizar mis clientes</strong>.
                                </>
                            )}
                        />
                    )}
                    {activeTab === 'dashboard' && (
                        <DashboardTab
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                            onNavigateToSaldos={() => setActiveTab('saldos')}
                        />
                    )}
                    {activeTab === 'saldos' && (
                        <ControlSaldosTab
                            clientesDb={clientesDb}
                            especies={especies}
                            onPrecargarCaucion={(data) => setCaucionModal({ open: true, prefill: data })}
                        />
                    )}
                    {activeTab === 'operaciones' && (
                        <ProductorOperaciones
                            {...tabsComunes}
                            cuentasJefferiesDb={{}}
                            mercadosDisponibles={MERCADOS_PRODUCTOR}
                        />
                    )}
                    {activeTab === 'cauciones' && (
                        <CaucionesTab
                            {...tabsComunes}
                            comitentePorCuit={{}}
                            onNavigateToSaldos={() => setActiveTab('saldos')}
                        />
                    )}
                    {activeTab === 'instrucciones' && (
                        <InstruccionesTab
                            {...tabsComunes}
                            attachmentFolder={getProductorAttachmentFolder(user.uid)}
                        />
                    )}
                    {activeTab === 'licitaciones' && (
                        <LicitacionesTab
                            {...tabsComunes}
                            datosFiscalesDb={datosFiscalesDb}
                        />
                    )}
                </MainContent>

                <CaucionFormModal
                    isOpen={caucionModal.open}
                    onClose={() => setCaucionModal({ open: false, prefill: null })}
                    clientesDb={clientesDb}
                    userRole={ROLES.COMERCIAL}
                    userName={userName}
                    userId={user.uid}
                    editingCaucion={null}
                    prefill={caucionModal.prefill}
                />

                <CommentModal
                    isOpen={commentModalData.isOpen}
                    onClose={() => setCommentModalData({ isOpen: false, title: '', content: '' })}
                    title={commentModalData.title}
                    content={commentModalData.content}
                />
            </AppWrapper>
        </SaldosClientesProvider>
    );
};

export default ProductorView;
